import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ExportDialog({ open, onOpenChange, title = "Esporta Excel", fields = null, dateFields = [], onConfirm }) {
  const allKeys = useMemo(() => (fields || []).map((f) => f.key), [fields]);
  const [selected, setSelected] = useState(allKeys);
  const [dateField, setDateField] = useState(dateFields[0]?.key || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (open) { setSelected(allKeys); setDateField(dateFields[0]?.key || ""); setFrom(""); setTo(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const allOn = fields && selected.length === allKeys.length;

  const confirm = () => {
    const params = {};
    if (fields) params.fields = selected.join(",");
    if (dateField && (from || to)) { params.date_field = dateField; if (from) params.date_from = from; if (to) params.date_to = to; }
    onConfirm(params);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="export-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          {dateFields.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Intervallo date</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={dateField} onValueChange={setDateField}>
                  <SelectTrigger className="w-52" data-testid="export-date-field"><SelectValue /></SelectTrigger>
                  <SelectContent>{dateFields.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="date" className="w-[8.5rem]" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-date-from" title="Da" />
                <span className="text-muted-foreground text-sm">→</span>
                <Input type="date" className="w-[8.5rem]" value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-date-to" title="A" />
              </div>
              <p className="text-[11px] text-muted-foreground">Lascia le date vuote per esportare tutti i record.</p>
            </div>
          )}
          {fields && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Campi da esportare</Label>
                <button type="button" className="text-xs text-primary hover:underline" data-testid="export-toggle-all" onClick={() => setSelected(allOn ? [] : allKeys)}>
                  {allOn ? "Deseleziona tutto" : "Seleziona tutto"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto rounded-md border border-border p-2">
                {fields.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent cursor-pointer" data-testid={`export-field-${f.key}`}>
                    <Checkbox checked={selected.includes(f.key)} onCheckedChange={() => toggle(f.key)} />{f.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="export-confirm" disabled={fields && selected.length === 0} onClick={confirm}>Esporta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const DATE_FIELDS_PRATICHE = [
  { key: "created_at", label: "Data caricamento" },
  { key: "updated_at", label: "Ultima modifica" },
  { key: "liquidated_at", label: "Data liquidazione" },
];
export const DATE_FIELDS_BASE = [
  { key: "created_at", label: "Data caricamento" },
  { key: "updated_at", label: "Ultima modifica" },
];
export const USER_EXPORT_FIELDS = [
  { key: "name", label: "Nome" }, { key: "email", label: "Email" }, { key: "role", label: "Ruolo" },
  { key: "cellulare", label: "Cellulare" }, { key: "telefono_fisso", label: "Telefono fisso" },
  { key: "citta_residenza", label: "Città" }, { key: "provincia", label: "Provincia" },
  { key: "codice_convenzione", label: "Codice convenzione" }, { key: "utenze_finanziarie", label: "Utenze finanziarie" },
  { key: "_sede", label: "Sede" }, { key: "_stato", label: "Stato" },
  { key: "_collaboratore", label: "Collaboratore" }, { key: "_segnalatori", label: "Segnalatore" },
];
export const LEAD_EXPORT_FIELDS = [
  { key: "nome", label: "Nome" }, { key: "cognome", label: "Cognome" }, { key: "azienda", label: "Azienda" },
  { key: "cellulare", label: "Cellulare" }, { key: "email", label: "E-Mail" }, { key: "codice_fiscale", label: "Codice Fiscale" },
  { key: "esito", label: "Esito" }, { key: "note", label: "Note" }, { key: "bene_finanziato", label: "Bene Finanziato" },
  { key: "importo_richiesto", label: "Importo Richiesto" }, { key: "rata", label: "Rata" }, { key: "durata", label: "Durata (mesi)" }, { key: "importo_residuo", label: "Importo Residuo" },
  { key: "scp", label: "SCP" }, { key: "cluster", label: "Cluster" }, { key: "eta_cliente", label: "Età cliente" },
  { key: "datore_lavoro", label: "Datore di Lavoro" }, { key: "professione", label: "Professione" }, { key: "indirizzo", label: "Indirizzo" },
  { key: "residenza", label: "Residenza" }, { key: "convenzionato", label: "Convenzionato" },
  { key: "provincia", label: "Provincia" }, { key: "cap", label: "Cap" }, { key: "campaign", label: "Campagna" },
];
export const PRACTICE_EXPORT_FIELDS = [
  { key: "numero", label: "Numero" }, { key: "numero_istituto", label: "N. Pratica Istituto" }, { key: "titolo", label: "Titolo" },
  { key: "tipo_finanziamento", label: "Tipo Finanziamento" }, { key: "importo", label: "Importo (€)" }, { key: "status", label: "Stato" },
  { key: "liquidazione", label: "Data Liquidazione" }, { key: "sede", label: "Sede" }, { key: "canale", label: "Canale" },
  { key: "origine", label: "Origine" }, { key: "anagrafiche", label: "Anagrafiche" },
  { key: "cliente", label: "Cliente" }, { key: "ragione_sociale", label: "Ragione Sociale" }, { key: "soggetto", label: "Soggetto" },
  { key: "email_cliente", label: "Email Cliente" }, { key: "telefono_cliente", label: "Telefono Cliente" },
  { key: "convenzionato", label: "Convenzionato" }, { key: "collaboratore", label: "Collaboratore" }, { key: "segnalatore", label: "Segnalatore" },
  { key: "istituto", label: "Istituto" }, { key: "provincia", label: "Provincia" },
  { key: "agente", label: "Agente" }, { key: "in_carico_a", label: "In carico a" },
  { key: "n_documenti", label: "N. Documenti" },
  { key: "prov_entrata", label: "Provvigioni Entrata (€)" }, { key: "prov_uscita", label: "Provvigioni Uscita (€)" },
  { key: "scadenza", label: "Scadenza" }, { key: "created_at", label: "Data Creazione" }, { key: "updated_at", label: "Ultimo Aggiornamento" },
];
export const ANAGRAFICHE_EXPORT_FIELDS = [
  { key: "tipo", label: "Tipo" }, { key: "nome", label: "Nome" }, { key: "cognome", label: "Cognome" },
  { key: "ragione_sociale", label: "Ragione Sociale" }, { key: "codice_fiscale", label: "Codice Fiscale" },
  { key: "partita_iva", label: "Partita IVA" }, { key: "cellulare", label: "Cellulare" },
  { key: "telefono", label: "Telefono" }, { key: "email", label: "E-Mail" }, { key: "indirizzo", label: "Indirizzo" },
  { key: "citta", label: "Città" }, { key: "provincia", label: "Provincia" }, { key: "cap", label: "CAP" },
  { key: "data_nascita", label: "Data Nascita" }, { key: "comune_nascita", label: "Comune Nascita" },
  { key: "provincia_nascita", label: "Provincia Nascita" }, { key: "occupazione", label: "Occupazione" },
  { key: "professione", label: "Professione" }, { key: "azienda_lavoro", label: "Azienda" },
  { key: "referente", label: "Referente" }, { key: "note", label: "Note" },
  { key: "created_at", label: "Creata il" },
];
export const CAMPAIGN_LEAD_EXPORT_FIELDS = [
  { key: "lead_code", label: "ID" }, { key: "nome", label: "Nome" }, { key: "cognome", label: "Cognome" }, { key: "azienda", label: "Azienda" },
  { key: "codice_fiscale", label: "Codice Fiscale" }, { key: "cellulare", label: "Cellulare" }, { key: "telefono", label: "Telefono" },
  { key: "email", label: "Email" }, { key: "_esito", label: "Esito" }, { key: "_nota_esito", label: "Ultima nota esito" }, { key: "_assegnato", label: "Assegnato a" },
  { key: "importo_richiesto", label: "Importo Richiesto" }, { key: "bene_finanziato", label: "Bene Finanziato" }, { key: "rata", label: "Rata" },
  { key: "durata", label: "Durata (mesi)" }, { key: "importo_residuo", label: "Importo Residuo" }, { key: "scp", label: "SCP" }, { key: "cluster", label: "Cluster" },
  { key: "eta_cliente", label: "Età cliente" }, { key: "datore_lavoro", label: "Datore di Lavoro" }, { key: "professione", label: "Professione" },
  { key: "indirizzo", label: "Indirizzo" }, { key: "residenza", label: "Residenza" }, { key: "citta", label: "Città" }, { key: "provincia", label: "Provincia" },
  { key: "cap", label: "Cap" }, { key: "convenzionato", label: "Convenzionato" }, { key: "note", label: "Note" },
  { key: "created_at", label: "Data inserimento" }, { key: "updated_at", label: "Ultima modifica" },
];
